import { useEffect } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { Event } from '@tribeplatform/gql-client/types'

import { ErrorBoundary } from '../../../common/components/ErrorBoundary/ErrorBoundary.js'
import { dayjs } from '../../../common/lib/dayjs.js'
import { FormDateTime } from '../../../Form/FormDateTime/FormDateTime.js'
import { Form } from '../../../Form/index.js'
import { T, useI18n } from '../../../i18n/index.js'

import { RecurrenceConfig } from './RecurrencConfig.js'

interface EventDatePickerProps {
  event?: Event
}

export const EventDatePicker = ({ event }: EventDatePickerProps) => {
  const { $t } = useI18n()
  const { setValue, watch } = useFormContext()
  const startTime = useWatch({ name: 'startTime' })

  useEffect(() => {
    const subscription = watch((formValues, { name }) => {
      if (name === 'startTime') {
        const { startTime, endTime } = formValues
        const isEndTimeLessThanAnHour =
          dayjs(endTime).diff(dayjs(startTime), 'minutes') <= 60
        /**
         * If the difference between start and end time is a large number (more than an hour),
         * we don't need to change the end time. For example, if the end time is one day from now,
         * better to not change the end time when the start time (hour:minute) is changed.
         */
        if (startTime && isEndTimeLessThanAnHour) {
          setValue('endTime', dayjs(startTime).add(1, 'hour').toDate(), {
            shouldDirty: true,
          })
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, setValue])
  const endDateMinTime = dayjs(startTime).add(5, 'minutes').toDate()

  return (
    <ErrorBoundary>
      <div className="flex flex-col gap-6">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4">
          <div className="min-w-0">
            <FormDateTime
              type="datetime-local-multi-input"
              name="startTime"
              dateFormat="M j, Y"
              className="text-content"
              label={<T id="Generics.Start" defaultMessage="Start" />}
              validation={{
                required: $t({
                  id: 'Generic.FieldIsRequired',
                  defaultMessage: 'This field is required.',
                }),
              }}
            />
          </div>
          <div className="min-w-0">
            <FormDateTime
              type="datetime-local-multi-input"
              name="endTime"
              dateFormat="M j, Y"
              className="text-content"
              minDate={endDateMinTime}
              label={<T id="Generics.End" defaultMessage="End" />}
              validation={{
                required: $t({
                  id: 'Generic.FieldIsRequired',
                  defaultMessage: 'This field is required.',
                }),
              }}
            />
          </div>
          <div className="col-span-full md:col-span-1">
            <Form.TimezonePicker
              dropdownPosition="bottom-end"
              name="timeZone"
              label={<T id="Generics.Timezone" defaultMessage="Timezone" />}
            />
          </div>
        </div>
        <RecurrenceConfig event={event} />
      </div>
    </ErrorBoundary>
  )
}
